// FILE: src/store/Slices/GroupSlice.jsx (FULLY UPDATED)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosServices from "../../services/axios";
import { setCalendarFilters } from "./CalendarFilterSlice";
import { fetchInstagramProfile, fetchfacebookProfile } from "../../services/group";
import { fetchAssets } from "./librarySlice";
import { getProfileAvatarUrl, withResolvedProfileAvatar } from "../../utils/profileAvatar";

const getStoredProfileDoc = (allProfiles, platform, profileId) => {
  return (allProfiles || []).find((profile) => {
    if (profile.platform !== platform) return false;
    if (String(profile.user_id || "") === String(profileId)) return true;
    if (String(profile.platform_id || "") === String(profileId)) return true;

    const pageDetails = Array.isArray(profile.page_details) ? profile.page_details : [];
    return pageDetails.some(
      (detail) =>
        String(detail?.accountId || "") === String(profileId) ||
        String(detail?.pageId || "") === String(profileId)
    );
  }) || null;
};

const getStoredPageDetail = (profileDoc, profileId = null) => {
  const pageDetails = Array.isArray(profileDoc?.page_details) ? profileDoc.page_details : [];
  if (!profileId) return pageDetails[0] || {};

  return (
    pageDetails.find(
      (detail) =>
        String(detail?.accountId || "") === String(profileId) ||
        String(detail?.pageId || "") === String(profileId)
    ) ||
    pageDetails[0] ||
    {}
  );
};

const getFacebookStoredPageDetail = (facebookMasterProfiles, userId, pageId) => {
  const masterProfile = (facebookMasterProfiles || []).find(
    (profile) =>
      profile.platform === "FACEBOOK" &&
      (
        String(profile.user_id || "") === String(userId) ||
        String(profile.platform_id || "") === String(userId)
      )
  );

  const pageDetails = Array.isArray(masterProfile?.page_details) ? masterProfile.page_details : [];
  return (
    pageDetails.find(
      (detail) =>
        String(detail?.pageId || "") === String(pageId) ||
        String(detail?.accountId || "") === String(pageId)
    ) || {}
  );
};

const getYoutubeStoredPageDetail = (masterYouTubeProfile, channelId) => {
  const pageDetails = Array.isArray(masterYouTubeProfile?.page_details)
    ? masterYouTubeProfile.page_details
    : [];

  return (
    pageDetails.find(
      (detail) =>
        String(detail?.pageId || "") === String(channelId) ||
        String(detail?.accountId || "") === String(channelId)
    ) || {}
  );
};

const isReconnectRequiredProfile = (profileDoc) => {
return Boolean(
profileDoc?.reauth_required ||
profileDoc?.token_status === "invalid" ||
profileDoc?.token_status === "reconnect_required"
);
};

const normalizeProfileDetails = (details = {}) => {
const merged = {
...details,
name:
details.name ||
details.profile ||
details.business_name ||
details.username ||
null,
username:
details.username ||
details.business_name ||
details.profile ||
details.name ||
null,
};
const resolved = withResolvedProfileAvatar(merged);
const resolvedAvatarUrl = getProfileAvatarUrl(resolved);
return {
...resolved,
profile_picture_url:
resolvedAvatarUrl ||
resolved.profile_picture_url ||
resolved.profile_picture_source_url ||
resolved.profile_image ||
null,
profile_image:
resolvedAvatarUrl ||
resolved.profile_image ||
resolved.profile_picture_url ||
null,
};
};

export const handleAuthSuccess = createAsyncThunk(
  "groups/handleAuthSuccess",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(fetchGroups()).unwrap();
      await dispatch(fetchAllProfileDetails()).unwrap();
      sessionStorage.removeItem("temp_selected_group_for_auth");
      return { success: true };
    } catch (error) {
      console.error("Error during post-auth data refresh:", error);
      sessionStorage.removeItem("temp_selected_group_for_auth");
      return rejectWithValue(
        error.message || "Failed to refresh data after connection."
      );
    }
  }
);

export const fetchAllProfileDetails = createAsyncThunk(
"groups/fetchAllProfileDetails",
async (requestedGroupName = null, { getState, rejectWithValue }) => {
try {
const { groups, selectedGroup } = getState().groups;
if (!groups || groups.length === 0) return {};

const activeGroups = groups.filter((group) => group.status !== "pending");
const storedSelectedGroup = localStorage.getItem("selected_group");

const targetGroup =
activeGroups.find((group) => group.name === requestedGroupName) ||
activeGroups.find((group) => group.name === selectedGroup) ||
activeGroups.find((group) => group.name === storedSelectedGroup) ||
activeGroups[0];

if (!targetGroup) return {};

const [allProfilesResponse, facebookProfilesResponse] = await Promise.all([
axiosServices.get("/profiles/get-profile"),
axiosServices
.post("/profiles/get-platform-profile", { platform: "FACEBOOK" })
.catch(() => ({ data: { profiles: [] } })),
]);

const allProfiles = allProfilesResponse?.data?.profiles || [];
const facebookMasterProfiles = facebookProfilesResponse?.data?.profiles || [];
const masterYouTubeProfiles = allProfiles.filter(
(profile) => profile.platform === "YOUTUBE_MASTER"
);
const masterYouTubeProfile = masterYouTubeProfiles[0] || null;

const allAccounts = targetGroup.connected_accounts || [];
const uniqueProfileIds = new Map();

allAccounts.forEach((account) => {
const key = String(
account.platform === "FACEBOOK"
? account.platform_pageId
: account.platform_id
);
if (key && key !== "undefined" && key !== "null" && !uniqueProfileIds.has(key)) {
uniqueProfileIds.set(key, account);
}
});

      const detailPromises = Array.from(uniqueProfileIds.values()).map(
        async (account) => {
          const key = String(
account.platform === "FACEBOOK"
? account.platform_pageId
: account.platform_id
);

          try {
            let details;

            if (account.platform === "INSTAGRAM") {
              const data = await fetchInstagramProfile(account.platform_id);
              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "INSTAGRAM",
                account.platform_id
              );
              const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

              details = normalizeProfileDetails({
                ...data,
                name: data?.name || data?.username || storedPage?.profile || account.profile,
                username: data?.username || data?.name || storedPage?.profile || account.profile,
                profile_picture_url:
                  data?.profile_picture_url ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_source_url:
                  data?.profile_picture_source_url ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_blob_url:
                  data?.profile_picture_blob_url ||
                  storedPage?.profile_picture_blob_url ||
                  null,
                profile_picture_blob_name:
                  data?.profile_picture_blob_name ||
                  storedPage?.profile_picture_blob_name ||
                  null,
                profile_picture_synced_at:
                  data?.profile_picture_synced_at ||
                  storedPage?.profile_picture_synced_at ||
                  null,
                profile_picture_version:
                  data?.profile_picture_version ||
                  storedPage?.profile_picture_version ||
                  null,
              });
            } else if (account.platform === "PINTEREST") {
              const response = await axiosServices.post("/pinterest/get-profile", {
                user_id: account.platform_id,
              });

              const apiRoot = response?.data || {};

              if (!apiRoot?.success || !apiRoot?.userAccount) {
                throw new Error("Invalid Pinterest profile response shape");
              }

              const userAccount = apiRoot.userAccount;

              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "PINTEREST",
                account.platform_id
              );
              const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

              const resolvedName =
                userAccount?.name ||
                userAccount?.business_name ||
                userAccount?.username ||
                storedPage?.profile ||
                account.profile ||
                "Pinterest";

              details = normalizeProfileDetails({
                ...storedPage,
                ...userAccount,
                id:
                  userAccount?.id ||
                  storedPage?.accountId ||
                  storedPage?.pageId ||
                  account.platform_id,
                name: resolvedName,
                username:
                  userAccount?.username ||
                  userAccount?.business_name ||
                  userAccount?.name ||
                  storedPage?.profile ||
                  account.profile ||
                  "Pinterest",
                business_name:
                  userAccount?.business_name ||
                  storedPage?.profile ||
                  account.profile ||
                  null,
                profile:
                  storedPage?.profile ||
                  userAccount?.business_name ||
                  userAccount?.username ||
                  account.profile ||
                  null,
                profile_image:
                  userAccount?.profile_picture_blob_url ||
                  userAccount?.profile_image ||
                  storedPage?.profile_picture_blob_url ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_url:
                  userAccount?.profile_picture_url ||
                  userAccount?.profile_image ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_source_url:
                  userAccount?.profile_picture_source_url ||
                  userAccount?.profile_image ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_blob_url:
                  userAccount?.profile_picture_blob_url ||
                  storedPage?.profile_picture_blob_url ||
                  null,
                profile_picture_blob_name:
                  userAccount?.profile_picture_blob_name ||
                  storedPage?.profile_picture_blob_name ||
                  null,
                profile_picture_synced_at:
                  userAccount?.profile_picture_synced_at ||
                  storedPage?.profile_picture_synced_at ||
                  null,
                profile_picture_version:
                  userAccount?.profile_picture_version ||
                  storedPage?.profile_picture_version ||
                  null,
              });
            } else if (account.platform === "LINKEDIN") {
            const storedProfile = getStoredProfileDoc(
            allProfiles,
            "LINKEDIN",
            account.platform_id
            );
            const storedPage = getStoredPageDetail(storedProfile, account.platform_id);
            if (storedPage && Object.keys(storedPage).length > 0) {
            details = normalizeProfileDetails({
            ...storedPage,
            name: storedPage.profile || account.profile,
            username: storedPage.profile || account.profile,
            profile_picture_url:
            storedPage.profile_picture_source_url ||
            storedPage.profile_picture_url ||
            null,
            error: isReconnectRequiredProfile(storedProfile) ? "TOKEN_INVALID" : null,
            token_status: storedProfile?.token_status || null,
            reauth_required: storedProfile?.reauth_required || false,
            });
  } else {
details = normalizeProfileDetails({
name: account.profile,
username: account.profile,
error: isReconnectRequiredProfile(storedProfile) ? "TOKEN_INVALID" : "Details not found",
token_status: storedProfile?.token_status || null,
reauth_required: storedProfile?.reauth_required || false,
});
}
} else if (account.platform === "LINKEDIN_COMPANY") {
const storedProfile = getStoredProfileDoc(
allProfiles,
"LINKEDIN_COMPANY",
account.platform_id
);
const storedPage = getStoredPageDetail(storedProfile, account.platform_id);
if (storedPage && Object.keys(storedPage).length > 0) {
details = normalizeProfileDetails({
...storedPage,
id: account.platform_id,
name: storedPage.profile || account.profile,
username: storedPage.profile || account.profile,
profile: storedPage.profile || account.profile,
profile_picture_url:
storedPage.profile_picture_blob_url ||
storedPage.profile_picture_source_url ||
storedPage.profile_picture_url ||
null,
profile_picture_source_url:
storedPage.profile_picture_source_url ||
storedPage.profile_picture_url ||
null,
profile_picture_blob_url:
storedPage.profile_picture_blob_url || null,
profile_picture_blob_name:
storedPage.profile_picture_blob_name || null,
profile_picture_synced_at:
storedPage.profile_picture_synced_at || null,
profile_picture_version:
storedPage.profile_picture_version || null,
error: isReconnectRequiredProfile(storedProfile) ? "TOKEN_INVALID" : null,
token_status: storedProfile?.token_status || null,
reauth_required: storedProfile?.reauth_required || false,
});
  } else {
details = normalizeProfileDetails({
id: account.platform_id,
name: account.profile,
username: account.profile,
profile: account.profile,
error: isReconnectRequiredProfile(storedProfile) ? "TOKEN_INVALID" : "Details not found",
token_status: storedProfile?.token_status || null,
reauth_required: storedProfile?.reauth_required || false,
});
}
} else if (account.platform === "GMB") {
              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "GMB",
                account.platform_id
              );
              const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

              details = normalizeProfileDetails({
                ...storedPage,
                name: storedPage.profile || account.profile,
                username: storedPage.profile || account.profile,
                profile_picture_url:
                  storedPage.profile_picture_source_url ||
                  storedPage.profile_picture_url ||
                  null,
              });
            } else if (account.platform === "YOUTUBE") {
const storedProfile = getStoredProfileDoc(
allProfiles,
"YOUTUBE",
account.platform_id
);

const linkedMasterProfile = storedProfile?.masterProfileId
? masterYouTubeProfiles.find(
(profile) =>
profile.platform === "YOUTUBE_MASTER" &&
String(profile._id) === String(storedProfile.masterProfileId)
)
: masterYouTubeProfile;

const storedPageFromChild = getStoredPageDetail(storedProfile, account.platform_id);
const storedPageFromMaster = getYoutubeStoredPageDetail(
linkedMasterProfile,
account.platform_id
);

const storedPage =
storedPageFromChild && Object.keys(storedPageFromChild).length > 0
? storedPageFromChild
: storedPageFromMaster;

const reconnectRequired =
isReconnectRequiredProfile(storedProfile) ||
isReconnectRequiredProfile(linkedMasterProfile);

const hasValidRefreshToken =
linkedMasterProfile?.refresh_token &&
linkedMasterProfile.refresh_token !== "Null";

if (reconnectRequired || !hasValidRefreshToken) {
details = normalizeProfileDetails({
name: storedPage?.profile || account.profile,
username: storedPage?.profile || account.profile,
profile: storedPage?.profile || account.profile,
profile_picture_url:
storedPage?.profile_picture_blob_url ||
storedPage?.profile_picture_source_url ||
storedPage?.profile_picture_url ||
null,
profile_picture_source_url:
storedPage?.profile_picture_source_url ||
storedPage?.profile_picture_url ||
null,
profile_picture_blob_url:
storedPage?.profile_picture_blob_url || null,
profile_picture_blob_name:
storedPage?.profile_picture_blob_name || null,
profile_picture_synced_at:
storedPage?.profile_picture_synced_at || null,
profile_picture_version:
storedPage?.profile_picture_version || null,
error: "TOKEN_INVALID",
token_status:
storedProfile?.token_status ||
linkedMasterProfile?.token_status ||
"reconnect_required",
reauth_required:
storedProfile?.reauth_required ||
linkedMasterProfile?.reauth_required ||
true,
});
} else {
const { data } = await axiosServices.post(
"/youtube/channel-details",
{
channelId: account.platform_id,
}
);

details = normalizeProfileDetails({
...data,
name: data?.title || storedPage?.profile || account.profile,
username: data?.title || storedPage?.profile || account.profile,
profile: data?.title || storedPage?.profile || account.profile,
profile_picture_url:
storedPage?.profile_picture_blob_url ||
data?.thumbnails?.high?.url ||
data?.thumbnails?.medium?.url ||
data?.thumbnails?.default?.url ||
storedPage?.profile_picture_source_url ||
storedPage?.profile_picture_url ||
null,
profile_picture_source_url:
storedPage?.profile_picture_source_url ||
storedPage?.profile_picture_url ||
data?.thumbnails?.high?.url ||
data?.thumbnails?.medium?.url ||
data?.thumbnails?.default?.url ||
null,
profile_picture_blob_url:
storedPage?.profile_picture_blob_url || null,
profile_picture_blob_name:
storedPage?.profile_picture_blob_name || null,
profile_picture_synced_at:
storedPage?.profile_picture_synced_at || null,
profile_picture_version:
storedPage?.profile_picture_version || null,
error: null,
token_status: "valid",
reauth_required: false,
});
}
            } else if (account.platform === "FACEBOOK") {
          const data = await fetchfacebookProfile({
            pageId: account.platform_pageId,
            user_id: account.platform_id,
          });

          const storedPage = getFacebookStoredPageDetail(
            facebookMasterProfiles,
            account.platform_id,
            account.platform_pageId
          );

          details = normalizeProfileDetails({
            ...storedPage,
            ...data,

            id: account.platform_pageId,
            pageId: account.platform_pageId,

            name:
              data?.name ||
              storedPage?.profile ||
              account.profile ||
              null,

            username:
              data?.username ||
              data?.name ||
              storedPage?.profile ||
              account.profile ||
              null,

            profile:
              storedPage?.profile ||
              data?.name ||
              account.profile ||
              null,

            profile_picture_url:
              data?.profile_picture_url ||
              storedPage?.profile_picture_source_url ||
              storedPage?.profile_picture_url ||
              null,

            profile_picture_source_url:
              data?.profile_picture_source_url ||
              storedPage?.profile_picture_source_url ||
              storedPage?.profile_picture_url ||
              null,

            profile_picture_blob_url:
              data?.profile_picture_blob_url ||
              storedPage?.profile_picture_blob_url ||
              null,

            profile_picture_blob_name:
              data?.profile_picture_blob_name ||
              storedPage?.profile_picture_blob_name ||
              null,

            profile_picture_synced_at:
              data?.profile_picture_synced_at ||
              storedPage?.profile_picture_synced_at ||
              null,

            profile_picture_version:
              data?.profile_picture_version ||
              storedPage?.profile_picture_version ||
              null,
          });
        } else if (account.platform === "TIKTOK") {
const storedProfile = getStoredProfileDoc(
allProfiles,
"TIKTOK",
account.platform_id
);
const storedPage = getStoredPageDetail(storedProfile, account.platform_id);
const reconnectRequired = isReconnectRequiredProfile(storedProfile);

if (storedPage && Object.keys(storedPage).length > 0) {
details = normalizeProfileDetails({
...storedPage,
name: storedPage.profile || account.profile,
username: storedPage.profile || account.profile,
profile: storedPage.profile || account.profile,
profile_picture_url:
storedPage.profile_picture_blob_url ||
storedPage.profile_picture_source_url ||
storedPage.profile_picture_url ||
null,
profile_picture_source_url:
storedPage.profile_picture_source_url ||
storedPage.profile_picture_url ||
null,
profile_picture_blob_url:
storedPage.profile_picture_blob_url || null,
profile_picture_blob_name:
storedPage.profile_picture_blob_name || null,
profile_picture_synced_at:
storedPage.profile_picture_synced_at || null,
profile_picture_version:
storedPage.profile_picture_version || null,
error: reconnectRequired ? "TOKEN_INVALID" : null,
token_status: storedProfile?.token_status || null,
reauth_required: storedProfile?.reauth_required || false,
last_token_error: storedProfile?.last_token_error || null,
});
} else {
details = normalizeProfileDetails({
name: account.profile,
username: account.profile,
profile: account.profile,
error: reconnectRequired ? "TOKEN_INVALID" : "Details not found",
token_status: storedProfile?.token_status || null,
reauth_required: storedProfile?.reauth_required || false,
last_token_error: storedProfile?.last_token_error || null,
});
}
}
        return { key, details };
          } catch (error) {
            console.warn(
              `Could not fetch details for profile ${key}:`,
              error.message
            );
            const errorType = error.response?.data?.errorType || null;

            return {
              key,
              details: normalizeProfileDetails({
                name: account.profile || "Profile Error",
                username: account.profile || "Profile Error",
                error: errorType,
              }),
            };
          }
        }
      );

      const results = await Promise.all(detailPromises);

      const profileDetailsMap = results.reduce((acc, { key, details }) => {
        if (key && details) {
          acc[String(key)] = details;
        }
        return acc;
      }, {});

      return profileDetailsMap;
    } catch (error) {
      console.error("[GroupSlice] fetchAllProfileDetails failed:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch profile details."
      );
    }
  }
);

export const fetchGroups = createAsyncThunk(
  "groups/fetchGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosServices.get("/group/get");
      const fetchedGroups = response.data.groups || [];
      return fetchedGroups;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const createGroup = createAsyncThunk(
  "groups/createGroup",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post("/group/create", payload);
      if (response.data.success) {
        return response.data;
      }
      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const deleteGroupDetails = createAsyncThunk(
  "groups/deleteGroupDetails",
  async (platform_id, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post("/group/delete-profile", {
        platform_id,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting group details:", error);
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const fetchGroupDetails = createAsyncThunk(
  "groups/fetchGroupDetails",
  async (groupName, { dispatch, rejectWithValue }) => {
    if (!groupName) {
      return rejectWithValue("Group name is required to fetch details.");
    }

    try {
      const response = await axiosServices.post("/group/get-particular", {
        groupName,
      });

      const accounts = response.data.group.connected_accounts || [];
      const uniqueProfiles = accounts;

      const allProfileIds = uniqueProfiles
        .map((profile) =>
          profile.platform === "FACEBOOK"
            ? profile.platform_pageId
            : profile.platform_id
        )
        .filter(Boolean);

      dispatch(setCalendarFilters({ profiles: allProfileIds }));
      return uniqueProfiles;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

const groupSlice = createSlice({
  name: "groups",
  initialState: {
    groups: [],
    loading: false,
    selectedGroup: localStorage.getItem("selected_group") || null,
    profile: [],
    profileDetails: {},
    currentProfileDetailsRequestId: null,
    error: null,
  },
  reducers: {
    _setSelectedGroup: (state, action) => {
      const groupName = action.payload;
      state.selectedGroup = groupName;

      if (groupName) {
        localStorage.setItem("selected_group", groupName);
      } else {
        localStorage.removeItem("selected_group");
      }
    },
    clearGroups: (state) => {
      state.groups = [];
      state.error = null;
    },
    setProfiles: (state, action) => {
      state.profile = action.payload;
    },
    updateGroupAndProfiles: (state, action) => {
      const updatedGroup = action.payload;
      if (!updatedGroup || !updatedGroup._id) return;
      const groupIndex = state.groups.findIndex(
      (group) => group._id === updatedGroup._id
      );
      if (groupIndex !== -1) {
      state.groups[groupIndex] = updatedGroup;
      }
      if (state.selectedGroup === updatedGroup.name) {
      state.profile = updatedGroup.connected_accounts;
      }
      },
      removeConnectedProfileFromSelectedGroup: (state, action) => {
      const { groupName, profile } = action.payload || {};

      if (!groupName || !profile) return;

      const profileIdToDelete =
      profile.platform === "FACEBOOK"
      ? profile.platform_pageId
      : profile.platform_id;

      if (!profile.platform || !profileIdToDelete) return;

      const shouldKeepAccount = (account) => {
      if (account.platform !== profile.platform) return true;

      const accountProfileId =
      account.platform === "FACEBOOK"
      ? account.platform_pageId
      : account.platform_id;

      return String(accountProfileId) !== String(profileIdToDelete);
      };

      const groupIndex = state.groups.findIndex((group) => group.name === groupName);

      if (groupIndex !== -1) {
      state.groups[groupIndex].connected_accounts = (
      state.groups[groupIndex].connected_accounts || []
      ).filter(shouldKeepAccount);
      }

      if (state.selectedGroup === groupName) {
      state.profile = (state.profile || []).filter(shouldKeepAccount);
      }
      },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch groups";
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        if (action.payload.success) {
          const updatedGroup = action.payload.group;
          const groupIndex = state.groups.findIndex(
            (group) => group._id === updatedGroup._id
          );

          if (groupIndex !== -1) {
            state.groups[groupIndex] = updatedGroup;
          } else {
            state.groups.push(updatedGroup);
          }

          if (state.selectedGroup === updatedGroup.name) {
            state.profile = updatedGroup.connected_accounts;
          }
        }
      })
      .addCase(fetchGroupDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGroupDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchGroupDetails.rejected, (state, action) => {
        state.loading = false;
        state.profile = [];
        state.error = action.payload;
      })
      .addCase(deleteGroupDetails.fulfilled, () => {})
      .addCase(fetchAllProfileDetails.pending, (state, action) => {
      state.loading = true;
      state.currentProfileDetailsRequestId = action.meta.requestId;
      })
      .addCase(fetchAllProfileDetails.fulfilled, (state, action) => {
      if (state.currentProfileDetailsRequestId !== action.meta.requestId) {
      return;
      }
      state.loading = false;
      state.currentProfileDetailsRequestId = null;
      state.profileDetails = action.payload || {};
      })
      .addCase(fetchAllProfileDetails.rejected, (state, action) => {
      if (state.currentProfileDetailsRequestId !== action.meta.requestId) {
      return;
      }
      state.loading = false;
      state.currentProfileDetailsRequestId = null;
      state.error = action.payload || "Failed to fetch profile details";
      });
  },
});

export const {
clearGroups,
_setSelectedGroup,
setProfiles,
updateGroupAndProfiles,
removeConnectedProfileFromSelectedGroup,
} = groupSlice.actions;

export const setSelectedGroup = (groupName) => (dispatch, getState) => {
  dispatch(_setSelectedGroup(groupName));
  const { groups } = getState().groups;

  if (groupName) {
    const selectedGroupData = groups.find((group) => group.name === groupName);
    if (selectedGroupData?._id) {
      dispatch(fetchAssets(selectedGroupData._id));
    } else {
      console.warn(
        `[GroupSlice] Could not find group ID for "${groupName}" to fetch assets.`
      );
    }
  }
};

export default groupSlice.reducer;